import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'

// PageHeader / PageHeadingPanel / Divider (design/components/page-header.html).
// PageHeader tops every product screen (the page's single <h1> + actions);
// PageHeadingPanel is the editorial hero (gradient em, marketing/index only);
// Divider separates sections. The future tag and gradient carry text, never
// colour alone.

export interface BreadcrumbItem {
  label: string
  href?: string
}

export interface PageHeaderProps {
  title: string
  subtitle?: string
  breadcrumb?: BreadcrumbItem[]
  actions?: ReactNode
  /** Orange "Phase 1.5/2" pill in the breadcrumb for not-yet-shipped routes. */
  futureTag?: '1.5' | '2'
  className?: string
}

/** Top of every product screen: the page's single <h1>, optional subtitle,
 *  breadcrumb nav and trailing actions. `futureTag` marks not-yet-shipped
 *  routes with a Phase 1.5/2 pill (text, not colour alone). */
export function PageHeader({
  title,
  subtitle,
  breadcrumb,
  actions,
  futureTag,
  className,
}: PageHeaderProps) {
  const { t } = useTranslation('components')
  return (
    <div className={cn('w-full', className)}>
      {breadcrumb && breadcrumb.length > 0 && (
        <nav aria-label={t('ui.breadcrumb')} className="mb-2">
          <ol className="flex flex-wrap items-center gap-[7px] text-[11px] text-text-3">
            {breadcrumb.map((c, i) => {
              const last = i === breadcrumb.length - 1
              return (
                <li key={i} className="flex items-center gap-[7px]">
                  {c.href && !last ? (
                    <a href={c.href} className="text-text-3 transition-colors hover:text-text-2">
                      {c.label}
                    </a>
                  ) : (
                    <span aria-current={last ? 'page' : undefined}>{c.label}</span>
                  )}
                  {!last && (
                    <span aria-hidden className="opacity-50">
                      /
                    </span>
                  )}
                </li>
              )
            })}
            {futureTag && (
              <li>
                <span className="rounded-pill bg-warn-bg px-1.5 py-px text-[9px] font-bold tracking-[0.04em] text-warn">
                  {t('ui.pageHeader.phase', { tag: futureTag })}
                </span>
              </li>
            )}
          </ol>
        </nav>
      )}

      <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-start sm:gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-[21px] font-extrabold leading-[1.15] tracking-[-0.025em] text-text">
            {title}
          </h1>
          {subtitle && <p className="mt-1 text-[12.5px] leading-normal text-text-2">{subtitle}</p>}
        </div>
        {actions && (
          // Las acciones ENVUELVEN y se encogen, y el título crece: con varias
          // acciones, `flex-none` aplastaba el título y recortaba la última
          // acción en 375px (el shell es overflow-x-hidden). Los Button ya
          // llevan whitespace-nowrap, así que envolver no parte una etiqueta.
          <div
            data-testid="page-header-actions"
            className="flex min-w-0 flex-wrap items-center justify-end gap-[9px] sm:ml-auto"
          >
            {actions}
          </div>
        )}
      </div>
    </div>
  )
}

export interface PageHeadingPanelProps {
  eyebrow?: string
  /** May contain <em> — it renders in the brand gradient. */
  title: ReactNode
  body?: ReactNode
  className?: string
}

export function PageHeadingPanel({ eyebrow, title, body, className }: PageHeadingPanelProps) {
  return (
    <div className={cn('py-1', className)}>
      {eyebrow && (
        <span className="inline-flex items-center gap-[9px] text-[11.5px] font-semibold uppercase tracking-[0.13em] text-text-3">
          <span
            aria-hidden
            className="h-px w-6 bg-[linear-gradient(90deg,var(--brand-blue),transparent)]"
          />
          {eyebrow}
        </span>
      )}
      <h2 className="mt-[13px] text-[clamp(26px,4vw,34px)] font-extrabold leading-[1.08] tracking-[-0.03em] text-text [&_em]:bg-[image:var(--brand-gradient)] [&_em]:bg-clip-text [&_em]:not-italic [&_em]:text-transparent">
        {title}
      </h2>
      {body && (
        <p className="mt-[11px] max-w-[60ch] text-[13.5px] leading-relaxed text-text-2">{body}</p>
      )}
    </div>
  )
}

export interface DividerProps {
  label?: string
  className?: string
}

/** Horizontal rule between sections; with `label` it becomes a role=separator
 *  with the uppercase label between two lines. */
export function Divider({ label, className }: DividerProps) {
  if (!label) {
    return <hr className={cn('h-px w-full border-0 bg-line', className)} />
  }
  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      className={cn(
        'flex items-center gap-3 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-text-3',
        'before:h-px before:flex-1 before:bg-line after:h-px after:flex-1 after:bg-line',
        className,
      )}
    >
      {label}
    </div>
  )
}
